import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal } from 'antd';
import { XAxis, YAxis, LineChart, CartesianGrid, ResponsiveContainer, Line, Legend, Tooltip } from 'recharts';
import { timeParse } from 'd3-time-format';
import L from 'leaflet';
import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';
import TimeSeriesMenu from './TimeSeriesMenu';

import { filterDatesWithinRange, sortResultByDateAsc } from 'utils/dateFunctions';
import { exportChartAsSVG, exportChartDataAsCSV } from 'utils/chartFileTransformation';
import useLeafletLayerManager from 'hooks/useLeafletLayerManager';
import { useNotificationHandler } from 'hooks/useNotificationHandler';
import homeService from 'services/homeService';
import $, { param } from 'jquery';
import "assets/styles/Home/HomeUI/TimeSeries/time-series-chart.css";

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

const chartStyles = {

    grid: {
        stroke: "#f5f5f5",
        strokeDasharray: "3 3",
    },
    axis: {
        stroke: "#333",
        fontSize: 12,
        tickSize: 10,
    },
    tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ccc",
        borderWidth: 1,
        borderRadius: 4,
        padding: 10,
        boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
        color: '#000'
    },
    legend: {
        fontSize: 14,
        fontWeight: "bold",
    }

};

const TILE_SIZE = 256; // The normal size of a tile layer

function fromLatLngToPoint(latLng) { // We use this function to convert the coordinates from latitude and longitude to points in the map
    var mercator = -Math.log(Math.tan((0.25 + latLng.lat / 360) * Math.PI));
    return {
        x: TILE_SIZE * (latLng.lng / 360 + 0.5),
        y: TILE_SIZE / 2 * (1 + mercator / Math.PI)
    };
}

function fromLatLngToTileCoord(latLng, zoom) { // we use this function to get the i/x and j/y index coordinates 
    var point = fromLatLngToPoint(latLng);// i and j are used for the index location of the point we have clicked in the tileLayer

    return {
        x: Math.floor(point.x * zoom / TILE_SIZE),
        y: Math.floor(point.y * zoom / TILE_SIZE),
        z: zoom
    };
}

const TimeSeriesChart = ({ tscCoords, mapRef, setIsTSCButtonClicked, tscModeRef, markerModeRef }) => {

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const [chartData, setChartData] = useState([]);
    const [activeLines, setActiveLines] = useState(null);
    const [selectedResolutions, setSelectedResolutions] = useState({});
    const [isChartShown, setIsChartShown] = useState(false);

    const {
        notifySuccess,
        notifyError,
        notifyLoading,
        closeNotification,
        contextHolder
    } = useNotificationHandler();

    const { createLeafletLayer, addLeafletLayer, removeLeafletLayer } = useLeafletLayerManager();

    const chartRef = useRef(null);
    const layerRefs = useRef([]);

    const parseDate = timeParse("%d/%m/%Y");

    useEffect(() => {
        return (() => {
            layerRefs.current.forEach((layer) => {
                mapRef.current.removeLayer(layer);
            });
            layerRefs.current = [];
        });
    }, []);

    const handleModalClose = () => {
        layerRefs.current.forEach((layer) => {
            mapRef.current.removeLayer(layer);
        });
        layerRefs.current = [];
    };

    function handleTSC(setIsTSCButtonClicked, tscModeRef, markerModeRef, setIsMarkerButtonClicked, newValue) {

        tscModeRef.current = newValue;
        setIsTSCButtonClicked(newValue);

        // Turn off marker mode if tscMode is turned off
        if (markerModeRef !== undefined) {
            if (newValue === false && markerModeRef.current) {
                setIsMarkerButtonClicked(false);
                markerModeRef.current = false;
            }
        }

    }

    const checkResult = (result) => {
        // Results come in this format ['esi', '08/05/2022', '-1.4249775']
        const layerName = result[0];
        const value = result[2];

        const layer = LayersAndResolutions.layers.find((layer) => layer.shortName == layerName);
        const range = layer.valueRange;
        return value >= range[0] && value <= range[1];
    }

    const handleLegendClick = (dataKey) => {
        setActiveLines(prevState => {
            if (prevState.includes(dataKey)) {
                return prevState.filter(line => line !== dataKey);
            } else {
                return [...prevState, dataKey];
            }
        });
    };

    const combineResultsByDate = (resultsArray) => {

        return resultsArray.reduce((acc, [layerName, date, value]) => {
            if (!acc[date]) {
                // Inicializar un nuevo registro para la fecha si no existe
                acc[date] = { date: date };
            }
            // Agregar el valor para la capa al registro de la fecha
            const layer = LayersAndResolutions.layers.find((layer) => layer.shortName == layerName);
            acc[date][layer.productName] = parseFloat(value);
            return acc;
        }, {});

    };

    const submitQuery = async () => {

    setIsChartShown(false);
    setLoading(true);

    const layersNames = LayersAndResolutions.layers.map((layer) => layer.longName);
    setActiveLines(layersNames);

    const loadingKey = notifyLoading(
        'Loading Chart Data',
        'Sit Back and Relax while we fetch your data...',
        0
    );

    if (!startDate || !endDate || !selectedResolutions || !tscCoords) {
        closeNotification(loadingKey);
        notifyError('Error', 'Please check the selected options');
        return;
    }

    const selectedLayersNames = Object.keys(selectedResolutions).filter(
        key => selectedResolutions[key]?.space && selectedResolutions[key]?.time
    );

    let layersDatesAndParameters = {};

    // -----------------------------
    // 1) FETCH ALL LAYER DATES FIRST
    // -----------------------------
    for (const layerName of selectedLayersNames) {
        const spaceResolution = selectedResolutions[layerName].space;
        const timeResolution = selectedResolutions[layerName].time;

        try {
            const params = [{
                folder: layerName,
                timeResolution,
                spaceResolution
            }];

            const dates = await homeService.getCommonDates(params);
            const uniqueDatesArray = Array.from(new Set(dates));
            const filteredDates = filterDatesWithinRange(uniqueDatesArray, startDate, endDate);

            if (filteredDates.length === 0) {
                notifyError('No Data Found', `We couldn't find any ${layerName} data for your selected dates`);
                continue;
            }

            layersDatesAndParameters[layerName] = {
                layerParameters: fileRecognize(layerName, spaceResolution, timeResolution),
                dates: filteredDates
            };

        } catch (error) {
            console.error(`Error fetching dates for ${layerName}:`, error);
        }
    }

    if (Object.keys(layersDatesAndParameters).length === 0) {
        closeNotification(loadingKey);
        setLoading(false);
        return;
    }

    // -------------------------------------
    // 2) BUILD ALL GETVALUE REQUEST PROMISES
    // -------------------------------------
    const promises = [];
    const resultsArray = [];
    const evt = { latlng: tscCoords, tsc: true };

    for (const layer in layersDatesAndParameters) {
        const { layerParameters, dates } = layersDatesAndParameters[layer];

        for (const date of dates) {
            const p = getValue(evt, layer, date, layerParameters)
                .then(result => {
                    if (checkResult(result)) resultsArray.push(result);
                })
                .catch(console.error);

            promises.push(p);
        }
    }

    // ------------------------
    // 3) WAIT FOR ALL REQUESTS
    // ------------------------
    Promise.all(promises).then(() => {

        if (resultsArray.length === 0) {
            closeNotification(loadingKey);
            setLoading(false);
            notifyError('Nothing Here', "This location doesn't have any data yet");
            return;
        }

        // -------------------
        // 4) PROCESS THE DATA
        // -------------------
        const combineResults = combineResultsByDate(resultsArray);
        const combineResultsArray = Object.values(combineResults);
        const sortedResults = sortResultByDateAsc(combineResultsArray);

        const sortedResultWithDateObject = sortedResults.map(item => ({
            ...item,
            dateObject: parseDate(item.date)
        }));

        setIsChartShown(true);
        setChartData(sortedResultWithDateObject);
        setLoading(false);

        closeNotification(loadingKey);
        notifySuccess("Chart Loaded", "Your chart has been loaded successfully");
    });
};

    const fileRecognize = (layerName, spaceResolution, timeResolution) => {
        return {
            provider: layerName.toLowerCase(),
            styles: '',
            transparent: true,
            version: '1.3.0',
            format: 'image/png',
            product: 'your_product_name', // Set appropriate default
            spaceresolution: spaceResolution,
            timeresolution: timeResolution,
            name: layerName,
        };
    }

    const getValue = (evt, layerName, date, layerParameters) => {
        const url = getFeatureInfoUrl(evt.latlng, layerName, date, layerParameters);

        return new Promise((resolve, reject) => {
            $.ajax({
                url: url,
                xhrFields: url.includes(process.env.REACT_APP_API_URL)
                    ? { withCredentials: true }
                    : null,
                success: function (data, status, xhr) {
                    data = JSON.stringify(data);
                    var err = typeof data === 'string' ? null : data;
                    var result = getResults(err, data, evt.tsc, layerParameters.provider, date);
                    resolve(result);
                },
                error: function (xhr, status, error) {
                    var result = getResults(error, null, evt.tsc, layerParameters.provider, date);
                    resolve(result);
                }
            });
        });
    }

    const layer_config = {
        "mdfapar": { scale_factor: 0.0001, nodata: -10 },
        "metref": { scale_factor: 0.01, nodata: -8000 },
        "mdmetv3": { scale_factor: 0.001, nodata: -1 },
        "mdfvc": { scale_factor: 0.0001, nodata: -10 }
    };
    const getResults = (err, content, tsc, provider, date, layerName) => {
        // Parse the date
        const dateJs = dayjs(date);
        const day = dateJs.format('DD');
        const month = dateJs.format('MM');
        const year = dateJs.format('YYYY');
        const dateString = `${day}/${month}/${year}`;
        const errorStr = 'Point out of extent or no data was returned';

        if (err && !tsc) {
            return [dateString, errorStr];
        }
        if (err) {
            console.error(err);
            return [provider, dateString, errorStr];
        }

        // Parse the content
        let value;
        try {
            value = JSON.parse(content).Value;
        } catch (e) {
            console.error('Error parsing content:', e);
            value = errorStr;
        }

        // --- APPLY SCALE FACTOR ------------------------------------
        const cfg = layer_config[provider];
        if (cfg && typeof value === "number") {
            if (value === cfg.nodata) {
                value = null; // or keep nodata
            } else {
                value = value * cfg.scale_factor;
            }
        }
        // ------------------------------------------------------------

        if (tsc) {
            return [provider, dateString, value];
        } else {
            return [dateString, value];
        }
    };


    const getFeatureInfoUrl = (latlng, layerName, date, layerParameters) => {
        // Get the map bounds and dimensions
        var bounds = mapRef.current.getBounds();
        var mapSize = mapRef.current._size;
        var mapWidth = mapSize.x;
        var mapHeight = mapSize.y;

        // Calculate resolution (per pixel) in map coordinates
        var resolutionX = (bounds.getEast() - bounds.getWest()) / mapWidth;
        var resolutionY = (bounds.getNorth() - bounds.getSouth()) / mapHeight;
        var viewResolution = Math.max(resolutionX, resolutionY); // Use the larger resolution for consistency

        // CRS and coordinates transformation based on the provider
        let transformedCoords, crs, query_layers, layers;

        const provider = layerParameters.provider;
        const eumetsatLayers = ["mdfvc", "mdfapar", "frm", "mdmetv3", "metref"];
        let newUrl;
        if (eumetsatLayers.includes(provider)) {
            const layer = LayersAndResolutions.layers.find((layer) => layer.longName == layerName);
            newUrl = `${process.env.REACT_APP_MAP_SERVER_URL}${layer.fileName}`;
            transformedCoords = { x: latlng.lng, y: latlng.lat }; // EPSG:4326
            crs = 'EPSG:3857';
            query_layers = 'productsInfo';
            layers = 'productsInfo';
        } /*else if (eumetsatLayers.includes(provider)) { 
         if (eumetsatLayers.includes(provider)) {
            newUrl = `${process.env.REACT_APP_API_URL}/api/eumetsat/feature_info`;
            transformedCoords = L.CRS.EPSG3857.project(latlng);
        } */ else {
            newUrl = `${process.env.REACT_APP_MAP_SERVER_URL}${layerParameters.provider}`;
            transformedCoords = L.CRS.EPSG3857.project(latlng);
            query_layers = 'products';
            layers = 'products';
        }

        // Calculate bounding box around the transformed coordinates
        var bbox = [
            transformedCoords.x - viewResolution / 2,
            transformedCoords.y - viewResolution / 2,
            transformedCoords.x + viewResolution / 2,
            transformedCoords.y + viewResolution / 2
        ];

        // Parse the date
        const dateJs = dayjs(date);
        const dayFormatted = dateJs.format('DD');
        const monthFormatted = dateJs.format('MM');
        const year = dateJs.year();

        // Construct the GetFeatureInfo request parameters
        var params = {
            "request": 'GetFeatureInfo',
            "service": 'WMS',
            "crs": 'EPSG:3857',
            "styles": '',
            "transparent": layerParameters.transparent,
            "version": '1.3.0',
            "format": layerParameters.format,
            "bbox": bbox.join(','), // Join the bounding box array to a string
            "height": mapRef.current._size.y, // Height of the map viewport
            "width": mapRef.current._size.x,  // Width of the map viewport
            "layers": layers,
            "query_layers": query_layers,
            "info_format": 'application/json',
            "dataType": 'raster',
            "provider": provider,
            "format": 'image/png',
            "product": layerParameters.product,
            "year": year,
            "month": monthFormatted,
            "day": dayFormatted,
            "spaceresolution": layerParameters.spaceresolution,
            "timeresolution": layerParameters.timeresolution,
            "name": layerParameters.name,
            "predictionDate": `${year}_${monthFormatted}_${dayFormatted}`,
        };

        // Get i and j coordinates from latlng and zoom
        let iAndJ = fromLatLngToTileCoord(latlng, mapRef.current._zoom);
        params[params.version === '1.3.0' ? 'i' : 'x'] = iAndJ.x;
        params[params.version === '1.3.0' ? 'j' : 'y'] = iAndJ.y;

        const buildQuery = () => {
            let str = "?";

            Object.entries(params).forEach(([key, value]) => {
                str += key + '=' + value + "&";
            });

            return str;
        }

        // Construct the full URL for the GetFeatureInfo request
        const url = (newUrl ? newUrl : layerParameters.url) // If it isn't null (it isn't a EUMETSAT layer), we use the layer's URL
            + buildQuery();

        return url;
    }


    return (
        <div className='tsc-container'>
            {contextHolder}
            <TimeSeriesMenu
                type={'chart'}
                selectedResolutions={selectedResolutions}
                setSelectedResolutions={setSelectedResolutions}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                loading={loading}
                submitQuery={submitQuery}
                handleFunction={handleTSC}
                setIsButtonClicked={setIsTSCButtonClicked}
                tscCoords={tscCoords}
                markerModeRef={markerModeRef}
                setIsMarkerButtonClicked={setIsTSCButtonClicked}
                tscModeRef={tscModeRef}
                mapRef={mapRef}
            />

            <Modal
                title="Chart and Data"
                open={isChartShown}
                onCancel={() => {
                    setIsChartShown(false);
                    handleModalClose();
                }}
                footer={null}
                centered
                width="90%"
                styles={{
                    body: {
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }
                }}
                zIndex={1500}
            >

                <div className="graph-container" ref={chartRef}>
                    <ResponsiveContainer style={{ backgroundColor: 'white' }} width="100%" height={400}>
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 20,
                                right: 80,
                                left: 80,
                                bottom: 20
                            }}
                        >
                            <CartesianGrid stroke={chartStyles.grid.stroke} strokeDasharray={chartStyles.grid.strokeDasharray} />
                            <XAxis
                                dataKey="date"
                                scale="category"
                                domain={['dataMin', 'dataMax']}
                                stroke={chartStyles.axis.stroke}
                                tick={{ fontSize: chartStyles.axis.fontSize }}
                            />
                            <Tooltip
                                contentStyle={chartStyles.tooltip}
                                formatter={(value) => (typeof value === 'number' ? value.toFixed(5) : value)}
                            />
                            <Legend onClick={(e) => handleLegendClick(e.dataKey)} wrapperStyle={chartStyles.legend} />
                            {
                                // Helper: Calculate Y-axis domains and ticks
                                (() => {
                                    const yAxisDomains = {};
                                    const yAxisTicks = {};
                                    if (chartData.length > 0) {
                                        const keys = Object.keys(
                                            chartData.reduce((acc, curr) => {
                                                Object.keys(curr).forEach(key => {
                                                    if (key !== 'date' && key !== 'undefined' && key !== 'dateObject') acc[key] = true;
                                                });
                                                return acc;
                                            }, {})
                                        );
                                        keys.forEach(key => {
                                            const values = chartData.map(item => item[key]).filter(v => typeof v === 'number' && !isNaN(v));
                                            if (values.length) {
                                                const min = Math.min(...values);
                                                const max = Math.max(...values);
                                                const padding = (max - min) * 0.1 || 1;
                                                yAxisDomains[key] = [min - padding, max + padding];

                                                // Calculate 5 evenly spaced ticks
                                                const step = (max - min) / 4;
                                                yAxisTicks[key] = [
                                                    min,
                                                    min + step,
                                                    min + 2 * step,
                                                    min + 3 * step,
                                                    max
                                                ];
                                            } else {
                                                yAxisDomains[key] = ['auto', 'auto'];
                                                yAxisTicks[key] = [];
                                            }
                                        });
                                    }
                                    return Object.keys(yAxisDomains).map((dataKey, index) => {
                                        const layer = LayersAndResolutions.layers.find(layer => layer.productName === dataKey);
                                        const yAxisId = `y-axis-${dataKey}`;
                                        const axisSide = index % 2 === 0 ? 'left' : 'right';
                                        return (
                                            <React.Fragment key={dataKey}>
                                                <YAxis
                                                    yAxisId={yAxisId}
                                                    orientation={axisSide}
                                                    stroke={layer?.tscColor || '#000'}
                                                    allowDataOverflow={true}
                                                    width={80}
                                                    domain={yAxisDomains[dataKey] || ['auto', 'auto']}
                                                    ticks={yAxisTicks[dataKey]}
                                                    tickFormatter={(value) => value.toFixed(2)}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey={dataKey}
                                                    stroke={layer?.tscColor || '#000'}
                                                    activeDot={{ r: 8 }}
                                                    dot={{ r: 4, fill: layer?.tscColor || '#000' }}
                                                    connectNulls={true}
                                                    hide={!activeLines.includes(layer.longName)}
                                                    yAxisId={yAxisId}
                                                />
                                            </React.Fragment>
                                        );
                                    });
                                })()
                            }
                        </LineChart>
                    </ResponsiveContainer>


                    <div className="tsc-buttons">
                        <Button onClick={() => exportChartAsSVG(chartRef, { notifySuccess, notifyError, notifyLoading, closeNotification })}>
                            EXPORT CHART
                        </Button>
                        <Button onClick={() => exportChartDataAsCSV(chartData, { notifySuccess, notifyError, notifyLoading, closeNotification })}>
                            EXPORT DATA
                        </Button>
                    </div>
                </div>

            </Modal>
        </div>
    );

}

export default TimeSeriesChart;